import type { DragEvent, ReactElement } from 'react'
import { isDefined, type DashboardDataWithEditable, type DashboardId, type DashboardScopeId } from '@terros/common'
import { Stack, Text } from '@mantine/core'
import { DashboardItem } from './DashboardItem'
import type { DashboardGroup } from './dashboardGrouping'

type Props = {
  dashboards: DashboardDataWithEditable[]
  deletion: DeletionProps
  group: DashboardGroup
  reorder: ReorderProps
  /** The scope being viewed; anything from a broader scope reads as "Shared". */
  scopeEntityId: DashboardScopeId | undefined
  title: string
  onDuplicate: (dashboard: DashboardDataWithEditable) => void
}

type ReorderProps = {
  draggingDashboardId?: DashboardId
  isReordering: boolean
  onDragEnd: () => void
  onDragOver: (event: DragEvent<HTMLDivElement>, group: DashboardGroup, index: number) => void
  onDragStart: (dashboard: DashboardDataWithEditable, index: number) => void
  onDrop: (event: DragEvent<HTMLDivElement>, group: DashboardGroup) => void
}

type DeletionProps = {
  deletingDashboardId?: DashboardId
  isRemoving: boolean
  onDelete: (dashboard: DashboardDataWithEditable) => void
}

export function DashboardGroupSection({
  dashboards,
  deletion,
  group,
  reorder,
  scopeEntityId,
  title,
  onDuplicate,
}: Props): ReactElement {
  return (
    <Stack gap='sm'>
      <Text className='font-semibold' size='lg'>
        {title}
      </Text>
      <Stack gap='xs'>
        {dashboards.map((dashboard, index) => (
          <DashboardItem
            dashboard={dashboard}
            deleting={deletion.isRemoving && deletion.deletingDashboardId === dashboard.dashboardId}
            dragging={reorder.draggingDashboardId === dashboard.dashboardId}
            isShared={isDefined(scopeEntityId) && dashboard.scopeId !== scopeEntityId}
            key={dashboard.dashboardId}
            reordering={reorder.isReordering}
            onDelete={deletion.onDelete}
            onDragEnd={reorder.onDragEnd}
            onDragOver={(event) => reorder.onDragOver(event, group, index)}
            onDragStart={() => reorder.onDragStart(dashboard, index)}
            onDrop={(event) => reorder.onDrop(event, group)}
            onDuplicate={onDuplicate}
          />
        ))}
      </Stack>
    </Stack>
  )
}
